import { Client, StompSubscription, IMessage } from '@stomp/stompjs';
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

// 권위: docs/ARCH_DASHBOARD.md §6.3 + §6.4
// - 단일 STOMP 연결을 애플리케이션 전역에서 공유 (모듈 스코프 싱글톤).
// - 지수 백오프: 1s → 2s → 4s → ... → 30s cap.
// - 인증은 CONNECT frame 의 `Authorization` 헤더로 Bearer 전달.
// - 재연결 성공 시 /api/v1/jobs/active 를 재조회하도록 listener 를 노출한다 (useRealtimeJobs 에서 처리).

export type ConnectionStatus = 'connected' | 'connecting' | 'reconnecting' | 'offline';

type StatusListener = (status: ConnectionStatus) => void;

interface Singleton {
  client: Client | null;
  status: ConnectionStatus;
  statusListeners: Set<StatusListener>;
  // 토픽 → 핸들러 세트 (동일 토픽에 여러 훅이 구독해도 Stomp subscribe 는 1번만)
  handlers: Map<string, Set<(msg: IMessage) => void>>;
  subs: Map<string, StompSubscription>;
  connectAttempt: number;
}

const singleton: Singleton = {
  client: null,
  status: 'offline',
  statusListeners: new Set(),
  handlers: new Map(),
  subs: new Map(),
  connectAttempt: 0,
};

function setStatus(next: ConnectionStatus): void {
  if (singleton.status === next) return;
  singleton.status = next;
  singleton.statusListeners.forEach((l) => l(next));
}

function backoffMs(attempt: number): number {
  // 1,2,4,8,16,30,30,...
  const base = Math.min(30_000, 1000 * Math.pow(2, Math.max(0, attempt - 1)));
  // 지터(±20%) 로 thundering herd 방지
  const jitter = base * (0.8 + Math.random() * 0.4);
  return Math.round(jitter);
}

function ensureClient(): Client {
  if (singleton.client) return singleton.client;

  // dev: Vite proxy /ws → ws://localhost:8080/ws
  // prod: same-origin 이므로 location.host 사용
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const brokerUrl = `${proto}//${window.location.host}/ws`;

  const client = new Client({
    brokerURL: brokerUrl,
    reconnectDelay: 0, // 아래에서 수동 백오프
    heartbeatIncoming: 10_000,
    heartbeatOutgoing: 10_000,
    connectHeaders: {},
    debug: () => {
      // 운영 환경에서는 조용히. 디버깅 필요 시 console.log 주입.
    },
  });

  client.beforeConnect = () => {
    const token = useAuthStore.getState().accessToken;
    client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  };

  client.onConnect = () => {
    singleton.connectAttempt = 0;
    setStatus('connected');
    // 재연결된 경우 등록된 모든 토픽에 다시 subscribe
    for (const [topic] of singleton.handlers) {
      resubscribe(topic);
    }
  };

  client.onWebSocketClose = () => {
    setStatus('reconnecting');
    scheduleReconnect();
  };

  client.onStompError = () => {
    setStatus('reconnecting');
    scheduleReconnect();
  };

  singleton.client = client;
  return client;
}

function scheduleReconnect(): void {
  if (!singleton.client) return;
  singleton.connectAttempt += 1;
  const delay = backoffMs(singleton.connectAttempt);
  setTimeout(() => {
    const client = singleton.client;
    if (!client) return;
    if (client.connected) return;
    try {
      client.deactivate();
    } catch {
      /* ignore */
    }
    client.activate();
  }, delay);
}

function resubscribe(topic: string): void {
  const client = singleton.client;
  if (!client || !client.connected) return;
  const prev = singleton.subs.get(topic);
  if (prev) {
    try {
      prev.unsubscribe();
    } catch {
      /* ignore */
    }
  }
  const sub = client.subscribe(topic, (msg) => {
    const handlers = singleton.handlers.get(topic);
    if (!handlers) return;
    handlers.forEach((h) => {
      try {
        h(msg);
      } catch {
        /* 핸들러 하나가 던져도 다른 핸들러는 유지 */
      }
    });
  });
  singleton.subs.set(topic, sub);
}

export function connectWebSocket(): void {
  const client = ensureClient();
  if (client.active) return;
  setStatus('connecting');
  client.activate();
}

export function disconnectWebSocket(): void {
  const client = singleton.client;
  if (!client) return;
  try {
    client.deactivate();
  } finally {
    singleton.subs.clear();
    setStatus('offline');
  }
}

/**
 * 특정 토픽에 subscribe. handler 는 메시지마다 호출된다.
 * 반환값은 unsubscribe 함수.
 */
export function subscribe(topic: string, handler: (msg: IMessage) => void): () => void {
  let set = singleton.handlers.get(topic);
  if (!set) {
    set = new Set();
    singleton.handlers.set(topic, set);
  }
  set.add(handler);

  // 이미 연결되어 있으면 즉시 subscribe, 아니면 onConnect 시 resubscribe 루프에서 처리됨
  if (singleton.client?.connected && !singleton.subs.has(topic)) {
    resubscribe(topic);
  }

  return () => {
    const s = singleton.handlers.get(topic);
    if (!s) return;
    s.delete(handler);
    if (s.size === 0) {
      singleton.handlers.delete(topic);
      const sub = singleton.subs.get(topic);
      if (sub) {
        try {
          sub.unsubscribe();
        } catch {
          /* ignore */
        }
        singleton.subs.delete(topic);
      }
    }
  };
}

/**
 * 컴포넌트 훅 — 현재 연결 상태를 React state 로 노출.
 * 첫 호출 시 연결을 보장한다.
 */
export function useWebSocket(): { status: ConnectionStatus } {
  const [status, setLocalStatus] = useState<ConnectionStatus>(singleton.status);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    connectWebSocket();

    const listener: StatusListener = (s) => {
      if (mountedRef.current) setLocalStatus(s);
    };
    singleton.statusListeners.add(listener);
    setLocalStatus(singleton.status);

    return () => {
      mountedRef.current = false;
      singleton.statusListeners.delete(listener);
      // 연결 자체는 해제하지 않음 (전역 공유).
    };
  }, []);

  return { status };
}

/**
 * 재연결 성공 시 한번씩 호출되는 콜백 등록. (useRealtimeJobs 가 /jobs/active 재조회용으로 사용)
 */
export function onReconnected(cb: () => void): () => void {
  const wrapper: StatusListener = (s) => {
    if (s === 'connected' && singleton.connectAttempt === 0) {
      // connectAttempt 이 0 으로 막 리셋된 직후 = 재연결 성공
      cb();
    }
  };
  singleton.statusListeners.add(wrapper);
  return () => {
    singleton.statusListeners.delete(wrapper);
  };
}
